import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react'
import { useProgramContent, useProgramContentMutations } from '@/api/queries'
import { StatusBadge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { ConfirmDialog } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import type { Content, ContentOwner, ContentPatch } from '@/types'

/** The API's names for required fields (`missing`), as the warning lists them. */
export const MISSING_LABELS: Record<string, string> = {
  headline: 'Headline',
  academicInfo: 'Academic info',
  outcomesPreface: 'Outcomes preface',
  outcomes: 'Outcomes',
  featuredQuote: 'Featured quote',
  image: 'Image',
}

export const missingLabel = (missing: string[]) => missing.map((m) => MISSING_LABELS[m] ?? m).join(', ')

interface Draft {
  headline: string
  academicInfo: string
  studentFacingValue: string
  outcomesPreface: string
  outcomes: string[]
  /** Typed or pasted ids; blank clears. */
  featuredQuoteId: string
  imageAssetId: string
}


function toDraft(c: Content | null): Draft {
  return {
    headline: c?.headline ?? '',
    academicInfo: c?.academicInfo ?? '',
    studentFacingValue: c?.studentFacingValue ?? '',
    outcomesPreface: c?.outcomesPreface ?? '',
    outcomes: c?.outcomes ?? [],
    featuredQuoteId: c?.featuredQuoteId ?? '',
    imageAssetId: c?.imageAssetId ?? '',
  }
}

/** Only what changed, as the API wants it: blank text clears. */
function diff(before: Draft, after: Draft): ContentPatch {
  const patch: ContentPatch = {}
  for (const key of ['headline', 'academicInfo', 'studentFacingValue', 'outcomesPreface'] as const) {
    if (after[key].trim() !== before[key].trim()) patch[key] = after[key].trim() || null
  }
  const outcomes = after.outcomes.map((o) => o.trim()).filter(Boolean)
  if (JSON.stringify(outcomes) !== JSON.stringify(before.outcomes)) patch.outcomes = outcomes
  for (const key of ['featuredQuoteId', 'imageAssetId'] as const) {
    if (after[key].trim() !== before[key]) patch[key] = after[key].trim() || null
  }
  return patch
}

/**
 * A program's content (PC-9, ADR-0018): copy, outcomes, featured quote and
 * image, with its status and "mark ready". Shared by the WB program drawer and
 * the slate content drawer.
 *
 * The featured quote and image are ids typed or pasted in: a copywriter finds
 * the quote or image through the MCP tools (`get_quotes`' `quote_id`,
 * `list_media`'s `asset_id`). Once saved, the quote and image show below, so
 * the id is checked by eye; the API refuses one that is malformed, not this
 * client's, or not approved, and the save says why.
 *
 * Saving sends only what changed. Marking ready with required content missing
 * asks first, then overrides (§ 3); saving a ready program that still misses
 * something keeps it ready and says what is missing (§ 4). `readOnly` shows a
 * slate program's inherited content: it is its WB program's to edit.
 */
export function ProgramContentEditor({
  clientId,
  owner,
  ownerId,
  readOnly = false,
}: {
  clientId: string
  owner: ContentOwner
  ownerId: string
  readOnly?: boolean
}) {
  const { data, isLoading } = useProgramContent(clientId, owner, ownerId)
  if (isLoading || !data) return <p className="text-[12px] text-ink-faint">Loading content…</p>
  // Keyed on the saved record, so a save or a refetch starts the draft from what was stored.
  return (
    <ContentForm
      key={`${data.content?.id ?? 'none'}:${data.content?.updatedAt ?? ''}`}
      clientId={clientId}
      owner={owner}
      ownerId={ownerId}
      content={data.content}
      status={data.status}
      missing={data.missing}
      readOnly={readOnly}
    />
  )
}

function ContentForm({
  clientId,
  owner,
  ownerId,
  content,
  status,
  missing,
  readOnly,
}: {
  clientId: string
  owner: ContentOwner
  ownerId: string
  content: Content | null
  status: Content['status']
  missing: string[]
  readOnly: boolean
}) {
  const ops = useProgramContentMutations(clientId, owner, ownerId)
  const toast = useToast()
  const saved = useMemo(() => toDraft(content), [content])
  const [draft, setDraft] = useState<Draft>(saved)
  const [confirmReady, setConfirmReady] = useState(false)
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const patch = diff(saved, draft)
  const dirty = Object.keys(patch).length > 0

  async function save() {
    try {
      const after = await ops.update.mutateAsync(patch)
      if (after.status === 'ready' && after.missing.length > 0) {
        toast.push(`Saved. Still ready, but missing: ${missingLabel(after.missing)}.`, 'error')
      } else {
        toast.push('Content saved.')
      }
    } catch (e) {
      toast.push(e instanceof Error ? e.message : 'Could not save the content.', 'error')
    }
  }

  async function setStatus(next: 'ready' | 'in_review', override: boolean) {
    try {
      await ops.setStatus.mutateAsync({ status: next, override })
      toast.push(next === 'ready' ? 'Marked ready to publish.' : 'Moved back to review.')
    } catch {
      toast.push('Could not change the status.', 'error')
    }
    setConfirmReady(false)
  }

  const moveOutcome = (i: number, step: -1 | 1) => {
    const next = [...draft.outcomes]
    ;[next[i], next[i + step]] = [next[i + step]!, next[i]!]
    set('outcomes', next)
  }

  const text = (label: string, key: 'headline' | 'outcomesPreface', hint?: string) => (
    <Field label={label} hint={hint}>
      {(p) => <Input {...p} value={draft[key]} disabled={readOnly} onChange={(e) => set(key, e.target.value)} />}
    </Field>
  )
  const long = (label: string, key: 'academicInfo' | 'studentFacingValue', hint?: string) => (
    <Field label={label} hint={hint}>
      {(p) => (
        <Textarea {...p} rows={4} value={draft[key]} disabled={readOnly} onChange={(e) => set(key, e.target.value)} />
      )}
    </Field>
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={status} />
        {content?.readyBy && status === 'ready' && (
          <span className="text-[12px] text-ink-faint">by {content.readyBy}</span>
        )}
        {missing.length > 0 && (
          <span className="text-[12px] text-warn">Missing: {missingLabel(missing)}</span>
        )}
      </div>

      {text('Headline', 'headline')}
      {long('Academic info', 'academicInfo', 'Shown with the program on the portal.')}
      {long('Student-facing value', 'studentFacingValue', 'Optional. The 2.0 Values table.')}
      {text('Outcomes preface', 'outcomesPreface', '“Career Paths:”, “Graduates work as:”')}

      <div className="space-y-1.5">
        <span className="field-label block">Outcomes</span>
        {draft.outcomes.length === 0 && <p className="text-[12px] text-ink-faint">No outcomes yet.</p>}
        <ol className="space-y-1.5">
          {draft.outcomes.map((outcome, i) => (
            <li key={i} className="flex items-center gap-1.5">
              <span className="w-5 text-right text-[12px] tabular text-ink-faint">{i + 1}.</span>
              <Input
                value={outcome}
                disabled={readOnly}
                aria-label={`Outcome ${i + 1}`}
                onChange={(e) => set('outcomes', draft.outcomes.map((o, j) => (j === i ? e.target.value : o)))}
              />
              {!readOnly && (
                <>
                  <IconButton label={`Move outcome ${i + 1} up`} disabled={i === 0} onClick={() => moveOutcome(i, -1)}>
                    <ArrowUp size={13} aria-hidden />
                  </IconButton>
                  <IconButton
                    label={`Move outcome ${i + 1} down`}
                    disabled={i === draft.outcomes.length - 1}
                    onClick={() => moveOutcome(i, 1)}
                  >
                    <ArrowDown size={13} aria-hidden />
                  </IconButton>
                  <IconButton
                    label={`Remove outcome ${i + 1}`}
                    onClick={() => set('outcomes', draft.outcomes.filter((_, j) => j !== i))}
                  >
                    <X size={13} aria-hidden />
                  </IconButton>
                </>
              )}
            </li>
          ))}
        </ol>
        {!readOnly && draft.outcomes.length < 20 && (
          <Button size="sm" onClick={() => set('outcomes', [...draft.outcomes, ''])}>
            <Plus size={13} aria-hidden />
            Add outcome
          </Button>
        )}
      </div>

      <Field
        label="Featured quote ID"
        hint="An approved quote's ID, as get_quotes returns it (quote_id). Blank for none."
      >
        {(p) => (
          <Input
            {...p}
            value={draft.featuredQuoteId}
            disabled={readOnly}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="font-mono text-[12px]"
            onChange={(e) => set('featuredQuoteId', e.target.value)}
          />
        )}
      </Field>
      {content?.featuredQuoteId && !content.featuredQuote && content.featuredQuoteId === draft.featuredQuoteId.trim() && (
        <p className="text-[12px] text-warn">This quote is no longer approved, so it is not shown.</p>
      )}
      {content?.featuredQuote && content.featuredQuote.id === draft.featuredQuoteId.trim() && (
        <blockquote className="rounded-[6px] border border-rule bg-surface-sunk px-3 py-2 text-[12px]">
          <p>“{content.featuredQuote.text}”</p>
          <footer className="mt-1 text-ink-muted">
            {[content.featuredQuote.studentName, content.featuredQuote.classYear, content.featuredQuote.majors]
              .filter(Boolean)
              .join(' · ')}
          </footer>
        </blockquote>
      )}

      <Field
        label="Image ID"
        hint="Blank uses the featured quote's image. Or an approved image's ID, as list_media returns it (asset_id)."
      >
        {(p) => (
          <Input
            {...p}
            value={draft.imageAssetId}
            disabled={readOnly}
            placeholder="Use the featured quote's image"
            className="font-mono text-[12px]"
            onChange={(e) => set('imageAssetId', e.target.value)}
          />
        )}
      </Field>
      {content?.image && !dirty && (
        <figure className="flex items-center gap-3">
          <img src={content.image.url} alt="" className="h-16 w-24 rounded-[4px] border border-rule object-cover" />
          <figcaption className="text-[12px] text-ink-faint">
            {content.image.source === 'override' ? 'Picked image' : 'From the featured quote'}
          </figcaption>
        </figure>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 border-t border-rule pt-3">
          <Button variant="primary" onClick={() => void save()} disabled={!dirty} loading={ops.update.isPending}>
            Save content
          </Button>
          {status === 'ready' ? (
            <Button onClick={() => void setStatus('in_review', false)} disabled={dirty}>
              Back to review
            </Button>
          ) : (
            <Button
              onClick={() => (missing.length > 0 ? setConfirmReady(true) : void setStatus('ready', false))}
              disabled={dirty || content === null}
              title={dirty ? 'Save first' : undefined}
            >
              Mark ready to publish
            </Button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmReady}
        onClose={() => setConfirmReady(false)}
        onConfirm={() => setStatus('ready', true)}
        title="Mark ready with content missing?"
        description={`Still missing: ${missingLabel(missing)}. It can be marked ready anyway and filled in later.`}
        confirmLabel="Mark ready anyway"
        busy={ops.setStatus.isPending}
      />
    </div>
  )
}
